import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { venposService } from '../../../../services/venposService';
import { useBooking, BOOKING_ACTIONS } from '../../../../contexts/BookingContext';
import { 
  getEffectiveVisitDate, 
  isToddlerRestrictedDate, 
  isToddlerSpecialUpsell 
} from '../../../../utils/visitDateUtils';
import {
  ticketRowIsCastleAndGardenEntry,
  ticketLineIsCastleAndGardenEntry,
  castleAndGardenPaidQtyForFreeAnimalParkPromotion,
  ticketRowIsHhaHistoricHouseMember,
  nonHhaMainTicketQty
} from '../../../../utils/animalParkUpsell';

const ANIMAL_PARK_PAGE_IDS = [
  'b0f1a62a-a34a-4c4e-a88d-6fda03a19279',
  '03c1719d-b7ff-43b1-acd9-72c6fdd56f6a',
  '360f48e1-8680-4719-9d2e-fc87a84c974b', // May Day bank holiday castle entry (4 May 2026)
  'b17520e4-20c1-451c-84ea-38c476c5fad7',
];

const isAnimalParkFreePromotion = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('animal park free') && n.includes('promotion');
};

const isAnimalParkOnlyLine = (name) =>
  (name || '').toLowerCase().includes('animal park only');

const isHalloweenCraftsUpsellName = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('halloween crafts');
};

const isWellnessDiscoveryFreeUpsell = (name) => {
  const n = String(name || '').toLowerCase();
  if (!n.includes('day of discovery')) return false;
  return n.includes('wellness') || n.includes('tree trail');
};

const HALLOWEEN_CRAFTS_DISPLAY_NAME =
  'Halloween Craft Sessions at The Belvoir Retail Village — Add £4.50';

// On non-Animal Park pages: hide these items in the modal
const isAnimalParkItemToHide = (name) => {
  const n = (name || '').toLowerCase();
  return (
    (n.includes('animal park free') && n.includes('promotion')) ||
    isAnimalParkOnlyLine(name)
  );
};

// Exclude Castle and Garden Entry + Garden and Playground (Only) Entry from upsell item list
const isExcludedUpsellItem = (name) => {
  const n = (name || '').toLowerCase().trim();
  return (
    n.includes('castle and garden entry - adult - entry') ||
    n.includes('castle and garden entry - child - entry') ||
    n.includes('castle and garden entry - concession - entry') ||
    n.includes('garden and playground only entry - adult - entry') ||
    n.includes('garden and playground only entry - child - entry') ||
    n.includes('garden and playground entry - concession - entry') ||
    n.includes('garden and playground only entry - concession - entry') ||
    n.includes('garden and playground only entry - concession')
  );
};

/** Castle & Gardens card or Pirate Adventure Trail upsell — same SKU hides in the modal */
const shouldApplyCastleGardenUpsellSkuHides = (parentUpsellName) => {
  const p = (parentUpsellName || '').toLowerCase();
  return ticketLineIsCastleAndGardenEntry(parentUpsellName) || p.includes('pirate adventure trail');
};

/**
 * Inside those upsell modals: hide upgrade/Friends/Family bundle SKUs not sold via this flow.
 */
const shouldHideCastleGardenUpsellModalItem = (parentUpsellName, itemName) => {
  if (!shouldApplyCastleGardenUpsellSkuHides(parentUpsellName)) return false;
  const n = (itemName || '').toLowerCase();

  if (n.includes('rhs upgrade')) return true;
  if (n.includes('garden to castle upgrade')) return true;
  if (n.includes('friends of belvoir')) return true;
  // Family bundles (Entry / ADV advance)
  if (n.includes('castle and garden entry') && n.includes('family')) return true;
  // Standalone "Castle upgrade" lines (not Castle & Gardens standard tickets)
  if (n.includes('castle upgrade') && !n.includes('castle and garden entry')) return true;

  return false;
};

/** Parent upsell card is Garden & Playground (not Castle & Gardens) */
const isGardenPlaygroundUpsellCardParentName = (name) => {
  const p = (name || '').toLowerCase();
  if (ticketLineIsCastleAndGardenEntry(name)) return false;
  if (p.includes('castle') && (p.includes('garden') || p.includes('gardens'))) return false;
  return p.includes('garden') && (p.includes('playground') || p.includes('play ground'));
};

const upsellModalItemSearchText = (item) =>
  `${item?.name || ''} ${item?.description || ''}`.toLowerCase();

/** Garden & Playground modal: hide Family ADV / Entry bundle SKUs */
const shouldHideGardenPlaygroundUpsellModalItem = (parentUpsellName, item) => {
  if (!isGardenPlaygroundUpsellCardParentName(parentUpsellName)) return false;
  const n = upsellModalItemSearchText(item);
  if (!n.includes('family')) return false;
  return (
    n.includes('garden and playground only') ||
    n.includes('garden and adventure playground only')
  );
};

/** Product line to hide inside the Afternoon Tea in The Aviary Tearoom upsell only */
const isAviaryAfternoonTeaUpsell = (upsellName) => {
  const n = (upsellName || '').toLowerCase();
  return n.includes('aviary tearoom') && n.includes('afternoon tea');
};

/** Regular Aviary tea, Festive Afternoon Tea, and any upsell whose items are tea sittings */
const isAfternoonTeaBookingLabel = (name) => {
  const n = (name || '').toLowerCase();
  if (!n) return false;
  return (
    n.includes('afternoon tea') ||
    n.includes('aviary tearoom') ||
    (n.includes('festive') && n.includes('tea'))
  );
};

/** Venue-level "Any Allergies?" questions — skipped for festive tea because notes are collected per guest */
const isAllergenEventQuestion = (question) => {
  const t = (
    question?.question ||
    question?.questionText ||
    question?.text ||
    question?.label ||
    ''
  ).toLowerCase();
  return t.includes('allerg');
};

const isFestiveAfternoonTeaLabel = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('festive') && n.includes('afternoon tea');
};

const isSantasGrottoCastleUpsell = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('grotto') && n.includes('castle') && !n.includes('village') && !n.includes('retail');
};

const isGentlemansAfternoonTeaProduct = (itemName) => {
  const n = (itemName || '').toLowerCase();
  return n.includes('gentleman') && n.includes('afternoon tea');
};

/** Gentleman's Afternoon Tea is only offered on 21 June 2026 (Father's Day UK) */
const isJune212026VisitDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === 2026 && date.getMonth() === 5 && date.getDate() === 21;
};

/**
 * Format a Date object as YYYY-MM-DD in local timezone
 * Avoids timezone conversion issues that occur with toISOString()
 */
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(16, 29, 64, 0.52);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999 !important;
  padding: 1rem;

  @media (max-width: 640px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 640px;
  width: 100%;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.9) inset,
    0 28px 72px rgba(16, 29, 64, 0.22),
    0 8px 24px rgba(16, 29, 64, 0.12);
  animation: modalIn 0.28s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes modalIn {
    from {
      opacity: 0;
      transform: translateY(14px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 640px) {
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
    animation: sheetIn 0.3s cubic-bezier(0.22, 1, 0.36, 1);

    @keyframes sheetIn {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    &::before {
      content: '';
      display: block;
      width: 36px;
      height: 4px;
      background: rgba(16, 29, 64, 0.14);
      border-radius: 2px;
      margin: 0.65rem auto 0;
    }
  }
`;

const ModalHeader = styled.div`
  padding: clamp(1.25rem, 3vw, 1.65rem) clamp(1.25rem, 3vw, 1.65rem) 1rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: clamp(0.9rem, 2.5vw, 1.25rem);
  right: clamp(0.9rem, 2.5vw, 1.25rem);
  width: 30px;
  height: 30px;
  border: 1px solid rgba(16, 29, 64, 0.14);
  background: rgba(16, 29, 64, 0.04);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: rgba(16, 29, 64, 0.48);
  transition: all 0.18s ease;

  &:hover {
    background: rgba(16, 29, 64, 0.08);
    color: rgba(16, 29, 64, 0.82);
    border-color: rgba(16, 29, 64, 0.22);
  }
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(1.3rem, 3vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 0.35rem;
  padding-right: 2.5rem;
`;

const ModalDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.52);
  font-size: clamp(0.86rem, 1.75vw, 0.94rem);
  line-height: 1.55;
  margin: 0;
`;

const ModalContent = styled.div`
  padding: clamp(1.15rem, 3vw, 1.5rem) clamp(1.25rem, 3vw, 1.65rem);
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.55rem;
  margin-bottom: 0.35rem;
`;

const NoItemsMessage = styled.div`
  text-align: center;
  padding: clamp(1.5rem, 4vw, 2rem) clamp(1rem, 3vw, 1.5rem);
  border: 1px solid rgba(16, 29, 64, 0.09);
  border-radius: 14px;
  color: rgba(16, 29, 64, 0.6);
  background: rgba(16, 29, 64, 0.02);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.85vw, 0.95rem);
  line-height: 1.55;
`;

const NoItemsAction = styled.button`
  margin-top: 1rem;
  padding: 0.65rem 1.35rem;
  border-radius: 999px;
  border: none;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.28);

  &:hover {
    background: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 18px rgba(16, 29, 64, 0.22);
    transform: translateY(-1px);
  }
`;

const ItemCard = styled.div`
  background: ${props => props.$isSelected ? props.theme.colors.offWhite : 'white'};
  border: 1px solid ${props => props.$isSelected ? 'rgba(157, 109, 73, 0.42)' : 'rgba(16, 29, 64, 0.1)'};
  border-radius: 12px;
  padding: clamp(0.65rem, 2vw, 0.95rem) clamp(0.75rem, 2vw, 1rem);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.18s ease, transform 0.18s ease;
  position: relative;
  box-shadow: ${props => props.$isSelected
    ? '0 1px 0 rgba(255,255,255,0.95) inset, 0 6px 20px rgba(157, 109, 73, 0.1)'
    : '0 1px 0 rgba(255,255,255,0.95) inset, 0 2px 8px rgba(16, 29, 64, 0.05)'};

  &:hover {
    border-color: rgba(157, 109, 73, 0.35);
    transform: translateY(-1px);
    box-shadow: 0 1px 0 rgba(255,255,255,0.95) inset, 0 8px 22px rgba(16, 29, 64, 0.09);
  }
`;

const ItemName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(0.94rem, 2.1vw, 1.06rem);
  font-weight: 700;
  margin: 0 0 0.25rem;
  line-height: 1.22;
  letter-spacing: -0.01em;
  padding-right: 1.65rem;
`;

const ItemDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.5);
  font-size: clamp(0.78rem, 1.65vw, 0.86rem);
  line-height: 1.4;
  margin: 0 0 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItemMetaNote = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.35rem;
  line-height: 1.35;
`;

const GrottoCastleNote = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.86rem, 1.75vw, 0.94rem);
  line-height: 1.5;
  color: #5c3d1e;
  background: #fef8ee;
  border: 1px solid rgba(157, 109, 73, 0.35);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  margin: 0 0 0.85rem;
`;

const ItemCardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem 0.65rem;
  margin-top: 0.3rem;
`;

const ItemPrice = styled.div`
  display: inline-flex;
  align-items: baseline;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.82rem, 1.75vw, 0.92rem);
  font-weight: 700;
  color: ${props => props.$wellness ? '#302d2c' : props.theme.colors.accent};
  padding: 0.2rem 0.52rem;
  background: ${props =>
    props.$wellness ? 'rgba(215, 183, 167, 0.35)' : 'rgba(157, 109, 73, 0.08)'};
  border-radius: 999px;
  border: 1px solid ${props =>
    props.$wellness ? 'rgba(178, 156, 141, 0.45)' : 'rgba(157, 109, 73, 0.18)'};
  flex-shrink: 0;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
`;

const OriginalPrice = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.92rem;
  font-weight: 500;
  color: rgba(16, 29, 64, 0.35);
  text-decoration: line-through;
`;

const DiscountedPrice = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1.05rem);
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
`;

const DiscountBadge = styled.span`
  background: linear-gradient(135deg, #f4d03f 0%, #d4af37 100%);
  color: #1a3a52;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FathersDaySpecialNote = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.25rem;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(16, 29, 64, 0.04);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 999px;
  padding: 0.18rem;
  width: fit-content;
  flex-shrink: 0;
`;

const QuantityButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  transition: all 0.18s ease;
  box-shadow: 0 1px 4px rgba(16, 29, 64, 0.1);
  touch-action: manipulation;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.offWhite};
    box-shadow: 0 2px 6px rgba(16, 29, 64, 0.14);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const QuantityInput = styled.input`
  width: 34px;
  text-align: center;
  border: none;
  background: transparent;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};

  &:focus {
    outline: none;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  -moz-appearance: textfield;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 999px;
  min-width: 24px;
  height: 24px;
  padding: 0 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 3px 10px rgba(157, 109, 73, 0.32);
  border: 2px solid rgba(255, 255, 255, 0.95);
`;

const ModalFooter = styled.div`
  padding: clamp(0.9rem, 2.5vw, 1.15rem) clamp(1.25rem, 3vw, 1.65rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: white;
  border-radius: 0 0 20px 20px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 0.65rem;
  }
`;

const TotalAmount = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.9vw, 0.97rem);
  font-weight: 600;
  color: ${props => props.isError ? '#b71c1c' : props.theme.colors.primary};

  @media (max-width: 480px) {
    text-align: center;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 0.6rem 1.15rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  border: none;
  white-space: nowrap;
  touch-action: manipulation;
`;

const CancelButton = styled(Button)`
  background: rgba(16, 29, 64, 0.05);
  color: rgba(16, 29, 64, 0.58);

  &:hover {
    background: rgba(16, 29, 64, 0.09);
    color: rgba(16, 29, 64, 0.82);
  }

  @media (max-width: 480px) {
    flex: 1;
  }
`;

const AddToBasketButton = styled(Button)`
  background: ${props => props.theme.colors.accent};
  color: white;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.28);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    box-shadow: 0 6px 20px rgba(16, 29, 64, 0.22);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    flex: 2;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  padding: clamp(2rem, 5vw, 3rem) 1.5rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 2vw, 0.98rem);
  color: rgba(16, 29, 64, 0.5);

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid rgba(16, 29, 64, 0.1);
    border-top-color: ${props => props.theme.colors.accent};
    border-radius: 50%;
    animation: modalSpin 0.85s linear infinite;
    flex-shrink: 0;
  }

  @keyframes modalSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TimeSelectionContainer = styled.div`
  margin-top: 0.5rem;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.7rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const TimeOption = styled.div`
  background: ${props => props.$isDisabled ? 'rgba(16,29,64,0.03)' : (props.$isSelected ? props.theme.colors.offWhite : 'white')};
  border: 1px solid ${props => props.$isDisabled ? 'rgba(16,29,64,0.07)' : (props.$isSelected ? 'rgba(157, 109, 73, 0.45)' : 'rgba(16, 29, 64, 0.1)')};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  text-align: center;
  opacity: ${props => props.$isDisabled ? 0.5 : 1};
  box-shadow: ${props => props.$isSelected
    ? '0 4px 14px rgba(157, 109, 73, 0.1)'
    : '0 1px 0 rgba(255,255,255,0.95) inset, 0 2px 8px rgba(16,29,64,0.05)'};

  &:hover {
    ${props => !props.$isDisabled && `
      border-color: rgba(157, 109, 73, 0.35);
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(16, 29, 64, 0.09);
    `}
  }
`;

const TimeDisplay = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1rem, 2.2vw, 1.08rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${props => props.$isDisabled ? 'rgba(16,29,64,0.3)' : props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const TimeDescription = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.82rem;
  color: ${props => props.$isDisabled ? 'rgba(16,29,64,0.3)' : 'rgba(16,29,64,0.52)'};
  margin-bottom: 0.28rem;
`;

const TimeAvailability = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: ${props => (props.$isDisabled || props.$isLimited) ? '600' : '500'};
  color: ${props => props.$isDisabled ? '#c62828' : (props.$isLimited ? '#e65100' : 'rgba(16,29,64,0.44)')};
`;

const BackButton = styled(Button)`
  background: transparent;
  color: rgba(16, 29, 64, 0.52);
  border: 1px solid rgba(16, 29, 64, 0.12);

  &:hover {
    background: rgba(16, 29, 64, 0.04);
    color: rgba(16, 29, 64, 0.8);
  }
`;

const QuestionsContainer = styled.div`
  margin-top: 0.25rem;
`;

const QuestionItem = styled.div`
  background: rgba(16, 29, 64, 0.02);
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  padding: clamp(0.9rem, 2.5vw, 1.1rem);
  margin-bottom: 0.8rem;
`;

const QuestionText = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 2vw, 0.97rem);
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.6rem;
`;

const QuestionInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(16, 29, 64, 0.14);
  border-radius: 8px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.94rem;
  background: white;
  transition: border-color 0.18s ease;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
`;

const QuestionTextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(16, 29, 64, 0.14);
  border-radius: 8px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.94rem;
  min-height: 78px;
  resize: vertical;
  transition: border-color 0.18s ease;

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
`;

const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 0.2rem;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: clamp(0.65rem, 2vw, 1rem);
`;

const StepDot = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 999px;
  background: ${props => props.$isActive ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.1)'};
  transition: background 0.25s ease;
`;

const ModifiersContainer = styled.div`
  margin-top: 0.25rem;
`;

const ModifierGroup = styled.div`
  background: rgba(16, 29, 64, 0.02);
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  padding: clamp(0.9rem, 2.5vw, 1.1rem);
  margin-bottom: 0.8rem;
`;

const ModifierPrompt = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 2vw, 0.97rem);
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.7rem;
`;

const ModifierOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.6rem;
`;

const ModifierOption = styled.div`
  background: ${props => props.$isSelected ? 'rgba(157, 109, 73, 0.07)' : 'white'};
  border: 1px solid ${props => props.$isSelected ? 'rgba(157, 109, 73, 0.42)' : 'rgba(16, 29, 64, 0.1)'};
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  cursor: pointer;
  transition: all 0.18s ease;
  text-align: center;

  &:hover {
    border-color: rgba(157, 109, 73, 0.35);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.07);
  }
`;

const ModifierName = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.86rem, 1.9vw, 0.93rem);
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.2rem;
`;

const ModifierPrice = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: rgba(16, 29, 64, 0.5);
  font-weight: 500;
`;

const AllergenNotesContainer = styled.div`
  margin-top: 0.9rem;
  padding: 0.85rem 1rem;
  background: rgba(255, 251, 230, 0.75);
  border: 1px solid rgba(201, 162, 39, 0.32);
  border-radius: 10px;
`;

const AllergenLabel = styled.label`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.4rem;
`;

const AllergenTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(16, 29, 64, 0.14);
  border-radius: 8px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(201, 162, 39, 0.6);
    box-shadow: 0 0 0 3px rgba(201, 162, 39, 0.1);
  }

  &::placeholder {
    color: rgba(16, 29, 64, 0.35);
  }
`;

const UpsellDetailModal = ({ 
  isOpen, 
  onClose, 
  upsell, 
  selectedDate,
  selectedTimeSlot,
  selectedTickets = [],
  locationId,
  pageId,
  requireAfternoonTea = false,
  onAddToBasket,
  onRemoveUpsell,
  loading = false,
  onChangeDateClick // New prop for going back to date selection
}) => {
  const { modifierSelections, dispatch } = useBooking();
  
  const [itemDetails, setItemDetails] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [eventTimes, setEventTimes] = useState(null);
  const [selectedEventTime, setSelectedEventTime] = useState(null);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [eventQuestions, setEventQuestions] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [participantDetails, setParticipantDetails] = useState(null);
  const [participantAnswers, setParticipantAnswers] = useState({});
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [productModifiers, setProductModifiers] = useState(null);
  const [localModifierSelections, setLocalModifierSelections] = useState({});
  const [allergenNotes, setAllergenNotes] = useState({}); // Store allergen notes per item: { "itemId-quantityIndex": "notes" }
  const [loadingModifiers, setLoadingModifiers] = useState(false);
  const [currentStep, setCurrentStep] = useState('items'); // 'items', 'times', 'questions', 'participants', or 'modifiers'
  
  const visitDate = useMemo(
    () => getEffectiveVisitDate(selectedDate, selectedTimeSlot, selectedTickets),
    [selectedDate, selectedTimeSlot, selectedTickets]
  );

  const hasCastleAndGardenEntryInBasket = useMemo(() => {
    return (selectedTickets || []).some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      return qty > 0 && ticketRowIsCastleAndGardenEntry(t);
    });
  }, [selectedTickets]);

  const paidCastleQtyForFreeAnimalParkPromo = useMemo(
    () => castleAndGardenPaidQtyForFreeAnimalParkPromotion(selectedTickets),
    [selectedTickets]
  );

  /** True when step-1 tickets are Animal Park products (not Castle/HHA-only journeys sharing an AP page id). */
  const hasAnimalParkMainTicketSelection = useMemo(() => {
    return (selectedTickets || []).some(t => {
      const qty = parseInt(t.quantity, 10) || 0;
      if (qty <= 0) return false;
      if (t.isUpsellItem || t.pageType === 'EventTemplate') return false;
      const name = (t.name || '').toLowerCase();
      return name.includes('animal park');
    });
  }, [selectedTickets]);

  const hasHhaHistoricHouseMemberInBasket = useMemo(
    () =>
      (selectedTickets || []).some(t => {
        const qty = parseInt(t.quantity, 10) || 0;
        return qty > 0 && ticketRowIsHhaHistoricHouseMember(t);
      }),
    [selectedTickets]
  );

  // Note: Circus discount is now handled via dynamic discount code at basket level
  // No need to show discount UI in modal - it will be applied in the basket

  const availableItems = useMemo(() => {
    let items = itemDetails?.webSaleItems || [];

    // Merchandise upsells: if getUpsellItemDetails returned nothing (or isn't supported),
    // synthesise a single item from the upsell card data so the quantity selector works.
    if (items.length === 0 && upsell?.pageType === 'Merchandise' && upsell?.id) {
      items = [{
        webSaleItemId: upsell.id,
        name: upsell.name || '',
        description: upsell.description || '',
        price: upsell.price ?? 0,
        imageUrl: upsell.imageUrl || '',
        minPurchaseQty: upsell.minPurchaseQty ?? 1,
        maxPurchaseQty: upsell.maxPurchaseQty ?? 999,
        webSaleItemTypeID: upsell.webSaleItemTypeID ?? 16,
        quantity: 0
      }];
    }

    // Toddler restriction: hide toddler-specific items on restricted dates
    if (visitDate && isToddlerRestrictedDate(visitDate)) {
      items = items.filter(item => !isToddlerSpecialUpsell(item.name));
    }

    // Animal Park upsell: "free promotion only" when paid Castle qualifies and basket has no HHA lines (mixed HHA + paid
    // shows all SKUs). Animal Park main-ticket journeys reuse page ids with castle flows — gate on main ticket selection.
    const upsellName = (upsell?.name || '').toLowerCase();
    const animalParkUpsellShowFreePromotionOnly =
      upsellName.includes('animal park') &&
      ((ANIMAL_PARK_PAGE_IDS.includes(pageId) && hasAnimalParkMainTicketSelection) ||
        (paidCastleQtyForFreeAnimalParkPromo > 0 && !hasHhaHistoricHouseMemberInBasket));

    if (animalParkUpsellShowFreePromotionOnly) {
      items = items.filter(item => isAnimalParkFreePromotion(item.name));
    } else if (
      !ANIMAL_PARK_PAGE_IDS.includes(pageId) &&
      !(paidCastleQtyForFreeAnimalParkPromo > 0 && hasHhaHistoricHouseMemberInBasket)
    ) {
      // On other pages: hide Animal Park Free / Animal Park Only unless mixed HHA + paid castle (show full card list)
      items = items.filter(item => !isAnimalParkItemToHide(item.name));
    }

    // Same page UUID as Animal Park journeys is used for some castle products — hide Animal Park Free / Animal Park Only
    // unless qualifying paid castle (without HHA) or Animal Park main tickets apply. Matches non–AP page behaviour.
    if (
      upsellName.includes('animal park') &&
      paidCastleQtyForFreeAnimalParkPromo === 0 &&
      !hasAnimalParkMainTicketSelection
    ) {
      items = items.filter(item => !isAnimalParkItemToHide(item.name));
    }

    // Never list "Animal Park Only" SKUs here (mixed HHA + paid castle used to skip the hide above).
    if (upsellName.includes('animal park')) {
      items = items.filter(item => !isAnimalParkOnlyLine(item.name));
    }

    // Exclude Castle and Garden Entry + Garden and Playground (Only) Entry
    items = items.filter(item => !isExcludedUpsellItem(item.name));

    items = items.filter(
      item => !shouldHideCastleGardenUpsellModalItem(upsell?.name, item.name)
    );

    items = items.filter(item => !shouldHideGardenPlaygroundUpsellModalItem(upsell?.name, item));

    if (isAviaryAfternoonTeaUpsell(upsell?.name)) {
      const allowGentlemans = visitDate && isJune212026VisitDate(visitDate);
      items = items.filter(item => {
        if (!isGentlemansAfternoonTeaProduct(item.name)) return true;
        return allowGentlemans;
      });
    }

    // Remove duplicate Circus tickets: prefer standard (non–Early Bird) SKUs when both exist
    // (e.g. "Circus - Adult" wins over "Early Bird Circus - Adult" on the same upsell card).
    if (upsellName.includes('circus')) {
      const seenTypes = new Set();
      const filteredItems = [];

      // First pass: standard Circus lines
      items.forEach(item => {
        const itemName = (item.name || '').toLowerCase();
        if (itemName.includes('early bird')) return;
        const baseType = itemName.includes('adult')
          ? 'adult'
          : itemName.includes('child')
            ? 'child'
            : item.name;
        seenTypes.add(baseType);
        filteredItems.push(item);
      });

      // Second pass: Early Bird only when no standard line for that type
      items.forEach(item => {
        const itemName = (item.name || '').toLowerCase();
        if (!itemName.includes('early bird')) return;
        const baseType = itemName.includes('adult')
          ? 'adult'
          : itemName.includes('child')
            ? 'child'
            : item.name;
        if (!seenTypes.has(baseType)) {
          seenTypes.add(baseType);
          filteredItems.push(item);
        }
      });

      items = filteredItems;
    }

    return items;
  }, [
    itemDetails,
    visitDate,
    pageId,
    upsell,
    paidCastleQtyForFreeAnimalParkPromo,
    hasAnimalParkMainTicketSelection,
    hasHhaHistoricHouseMemberInBasket
  ]);

  // Main step tickets only (featured tour cap)
  const mainTicketCount = useMemo(() => {
    const mainTickets = (selectedTickets || []).filter(
      t => !t.isUpsellItem && t.pageType !== 'EventTemplate'
    );
    return mainTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
  }, [selectedTickets]);

  const nonHhaMainTicketCount = useMemo(
    () => nonHhaMainTicketQty(selectedTickets),
    [selectedTickets]
  );

  /** Free promotion only: capped by qualifying Castle & Gardens or Garden & Playground count; fallback uses non-HHA mains. */
  const animalParkFreePromotionMaxCount = useMemo(() => {
    if (paidCastleQtyForFreeAnimalParkPromo > 0) return paidCastleQtyForFreeAnimalParkPromo;
    return nonHhaMainTicketCount;
  }, [paidCastleQtyForFreeAnimalParkPromo, nonHhaMainTicketCount]);

  const isAnimalParkUpsell = useMemo(() => {
    const upsellName = (upsell?.name || '').toLowerCase();
    if (!upsellName.includes('animal park')) return false;
    return ANIMAL_PARK_PAGE_IDS.includes(pageId) || paidCastleQtyForFreeAnimalParkPromo > 0;
  }, [pageId, upsell?.name, paidCastleQtyForFreeAnimalParkPromo]);

  const isFeaturedTourUpsell = useMemo(() => {
    const name = (upsell?.name || '').toLowerCase();
    return name.includes('crown tour') || name.includes('butler saw');
  }, [upsell?.name]);

  const isRequiredAfternoonTeaUpsell = requireAfternoonTea && isAfternoonTeaBookingLabel(upsell?.name);

  const otherAfternoonTeaQtyInBasket = useMemo(() => {
    if (!isRequiredAfternoonTeaUpsell) return 0;
    return (selectedTickets || []).reduce((sum, t) => {
      const isTea =
        isAfternoonTeaBookingLabel(t.name) ||
        isAfternoonTeaBookingLabel(t.parentUpsellName) ||
        isAfternoonTeaBookingLabel(t.parentUpsellName);
      if (!isTea) return sum;
      const parentId = t.parentUpsellId || t.pageId;
      if (upsell?.id && parentId === upsell.id) return sum;
      return sum + (parseInt(t.quantity, 10) || 0);
    }, 0);
  }, [isRequiredAfternoonTeaUpsell, selectedTickets, upsell?.id]);

  const afternoonTeaPartyCap = Math.max(0, mainTicketCount - otherAfternoonTeaQtyInBasket);

  const getMaxQuantityForItem = (item) => {
    if (isRequiredAfternoonTeaUpsell) {
      const otherSelectedInModal = Object.entries(selectedItems).reduce((sum, [id, qty]) => {
        if (String(id) === String(item?.webSaleItemId)) return sum;
        return sum + (parseInt(qty, 10) || 0);
      }, 0);
      const remaining = Math.max(0, afternoonTeaPartyCap - otherSelectedInModal);
      return Math.min(remaining, item?.maxPurchaseQty || 50);
    }
    if (isFeaturedTourUpsell) {
      return Math.min(mainTicketCount, item?.maxPurchaseQty || 50);
    }
    if (isAnimalParkUpsell && isAnimalParkFreePromotion(item?.name)) {
      return Math.min(animalParkFreePromotionMaxCount, item?.maxPurchaseQty || 50);
    }
    return item?.maxPurchaseQty || 50;
  };

  const formattedVisitDate = useMemo(() => {
    if (!visitDate) return null;
    return visitDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [visitDate]);

  useEffect(() => {
    if (!itemDetails) return;

    setSelectedItems(prev => {
      let changed = false;
      const allowedIds = new Set(availableItems.map(item => item.webSaleItemId));
      const next = Object.entries(prev).reduce((acc, [itemId, quantity]) => {
        if (allowedIds.has(itemId)) {
          acc[itemId] = quantity;
        } else {
          changed = true;
        }
        return acc;
      }, {});

      return changed ? next : prev;
    });
  }, [itemDetails, availableItems]);
  
  // Ref for auto-scrolling to continue button
  const navigationRef = useRef(null);

  // Initialize state when modal opens; hydrate from existing selections if present
  useEffect(() => {
    if (!isOpen) return;

    // Always reset server-driven pieces
      setItemDetails(null);
      setEventTimes(null);
      setEventQuestions(null);
    setParticipantDetails(null);
      setProductModifiers(null);
      setCurrentStep('items');

    // Attempt to hydrate from already-selected upsell tickets
    try {
      const existingUpsellTickets = (selectedTickets || []).filter(t => (
        t.isUpsellItem || t.pageType === 'EventTemplate'
      ))
      .filter(t => t.parentUpsellId === upsell?.id || t.pageId === upsell?.id);

      if (existingUpsellTickets.length === 0) {
        // No existing data; start fresh
        setSelectedItems({});
        setSelectedEventTime(null);
        setQuestionAnswers({});
        setParticipantAnswers({});
        setLocalModifierSelections({});
        setAllergenNotes({});
        return;
      }

      // 1) Quantities per webSaleItemId
      const quantities = {};
      existingUpsellTickets.forEach(ticket => {
        const id = ticket.webSaleItemId;
        if (!id) return;
        quantities[id] = (quantities[id] || 0) + (ticket.quantity || 1);
      });
      setSelectedItems(quantities);

      // 2) Selected time (use the first ticket's selectedEventTime if present)
      const ticketWithTime = existingUpsellTickets.find(t => t.selectedEventTime || t.selectedDate);
      if (ticketWithTime) {
        const hydratedTime = ticketWithTime.selectedEventTime || {
          eventId: ticketWithTime.eventId || null,
          eventCapacityUsageId: ticketWithTime.eventCapacityUsageId || null,
          startDateTime: ticketWithTime.selectedDate || null,
          reservationExpiryDateTime: ticketWithTime.reservationExpiryDateTime || null
        };
        setSelectedEventTime(hydratedTime);
      } else {
        setSelectedEventTime(null);
      }

      // 3) Prefill question answers from the first ticket if available
      const withQuestions = existingUpsellTickets.find(t => t.questionAnswers && Object.keys(t.questionAnswers).length > 0);
      setQuestionAnswers(withQuestions ? (withQuestions.questionAnswers || {}) : {});

      // 4) Prefill modifier selections per person (per itemId and index order)
      const modifierSelectionsMap = {};
      const perItemCounters = {};
      existingUpsellTickets
        .sort((a, b) => String(a.webSaleItemId).localeCompare(String(b.webSaleItemId)))
        .forEach(ticket => {
          const itemId = ticket.webSaleItemId;
          const currentIndex = perItemCounters[itemId] || 0;
          perItemCounters[itemId] = currentIndex + 1;
          if (ticket.modifierSelection && ticket.modifierSelection.modifierTextId) {
            const key = `${itemId}-${currentIndex}`;
            modifierSelectionsMap[key] = {
              modifierId: ticket.modifierSelection.modifierId,
              modifierTextId: ticket.modifierSelection.modifierTextId,
              quantityIndex: currentIndex,
              itemId
            };
          }
        });
      setLocalModifierSelections(modifierSelectionsMap);

      // Restore allergen notes per person using the same index-counter approach
      // as modifier selections, so keys match "${itemId}-${quantityIndex}" format.
      const allergenNotesMap = {};
      const allergenItemCounters = {};
      existingUpsellTickets
        .sort((a, b) => String(a.webSaleItemId).localeCompare(String(b.webSaleItemId)))
        .forEach(ticket => {
          const itemId = ticket.webSaleItemId;
          const currentIndex = allergenItemCounters[itemId] || 0;
          allergenItemCounters[itemId] = currentIndex + 1;
          if (ticket.allergenNotes) {
            allergenNotesMap[`${itemId}-${currentIndex}`] = ticket.allergenNotes;
          }
        });
      setAllergenNotes(allergenNotesMap);

      // 5) Restore participant answers (person names) if available
      const withParticipants = existingUpsellTickets.find(t => t.participantAnswers && Object.keys(t.participantAnswers).length > 0);
      if (withParticipants && withParticipants.participantAnswers) {

        setParticipantAnswers(withParticipants.participantAnswers);
      } else {
        setParticipantAnswers({});
      }

    } catch (e) {

      // In case of any hydration issues, fall back to clean state
      setSelectedItems({});
      setSelectedEventTime(null);
      setQuestionAnswers({});
      setParticipantAnswers({});
      setLocalModifierSelections({});
      setAllergenNotes({});
    }
  }, [isOpen, upsell, selectedTickets]);
  
  // Clear questions when selected time changes (user picks a different time)
  // DISABLED: This was causing questions to be cleared immediately after loading
  // useEffect(() => {
  //   if (currentStep === 'questions' && selectedEventTime) {
  //     // Time changed while on questions step, clear and reload
  //     
  //     setEventQuestions(null);
  //     setQuestionAnswers({});
  //   }
  // }, [selectedEventTime, currentStep]);

  // Load item details when modal opens
  useEffect(() => {
    const loadItemDetails = async () => {
      if (isOpen && upsell && !itemDetails) {
        setLoadingDetails(true);
        try {
          const response = await venposService.getUpsellItemDetails(
            '978', // locationId
            upsell.id,
            upsell.pageType || 'EventTemplate',
            'OnScreenCalendar',
            formatDateLocal(selectedDate) || ''
          );
          
          if (response?.data) {
            setItemDetails(response.data);
          }
        } catch (error) {

        } finally {
          setLoadingDetails(false);
        }
      }
    };

    loadItemDetails();
  }, [isOpen, upsell, itemDetails, selectedDate]);

  // Auto-scroll to continue button when time is selected
  useEffect(() => {
    if (selectedEventTime && currentStep === 'times' && navigationRef.current) {
      setTimeout(() => {
        navigationRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest'
        });
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [selectedEventTime, currentStep]);

  const handleItemQuantityChange = (itemId, quantity) => {
    const item = availableItems.find(i => i.webSaleItemId === itemId);
    const maxAllowed = getMaxQuantityForItem(item);
    const newQuantity = Math.max(0, Math.min(quantity, maxAllowed));
    
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  const incrementQuantity = (itemId) => {
    const currentQuantity = selectedItems[itemId] || 0;
    handleItemQuantityChange(itemId, currentQuantity + 1);
  };

  const decrementQuantity = (itemId) => {
    const currentQuantity = selectedItems[itemId] || 0;
    handleItemQuantityChange(itemId, currentQuantity - 1);
  };

  const handleQuantityInputChange = (itemId, value) => {
    const quantity = parseInt(value) || 0;
    handleItemQuantityChange(itemId, quantity);
  };

  const calculateTotal = () => {
    if (!availableItems.length) return 0;
    
    return availableItems.reduce((total, item) => {
      const quantity = selectedItems[item.webSaleItemId] || 0;
      return total + (item.price * quantity);
    }, 0);
  };

  const loadEventTimes = async (selectedItemsList) => {
    if (!upsell || !selectedDate) return;

    setLoadingTimes(true);
    try {
      // Prepare items for the API call
      const items = selectedItemsList.map(item => ({
        pageType: 'WebPage',
        pageId: upsell.parentWebSaleItemId || pageId,
        webSaleItemId: item.webSaleItemId,
        quantity: item.quantity,
        webSaleItemTypeID: item.webSaleItemTypeID,
        name: item.name,
        selectedDate: `${formatDateLocal(selectedDate)}T10:00:00`,
        reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        eventCapacityUsageId: null, // Will be populated by GetTimes response
        eventId: null, // Will be populated by GetTimes response
        fixedDonationAmount: 0,
        giftAidEligible: false,
        isSysGift: false,
        isSysMemberCard: false
      }));

      const response = await venposService.getUpsellEventTimes(
        String(locationId),
        String(pageId),
        upsell.id,
        upsell.pageType || 'EventTemplate',
        formatDateLocal(selectedDate) + 'T10:00:00',
        items
      );

      if (response?.data?.events && response.data.events.length > 0) {
        setEventTimes(response.data);
        setCurrentStep('times');
      } else {
        // No time slots (e.g. Merchandise upsells) — add directly to basket
        handleAddToBasket();
      }
    } catch (error) {

      // Fallback: proceed without time selection
      handleAddToBasket();
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleAddToBasket = () => {

    const itemsToAdd = Object.entries(selectedItems)
      .filter(([_, quantity]) => quantity > 0)
      .flatMap(([itemId, quantity]) => {
        const item = availableItems.find(i => i.webSaleItemId === itemId);
        if (!item) return [];

        // Create separate items for each quantity with their specific modifier selections
        return Array.from({ length: quantity }, (_, quantityIndex) => {
          const modifierKey = `${itemId}-${quantityIndex}`;
          const localModifierSelection = localModifierSelections[modifierKey];
          
          // Enrich modifier selection with text fields from booking context
          // The booking context has modifierName, modifierPrompt, etc.
          const contextModifierKey = `${itemId}-${quantityIndex}`;
          const contextModifierData = modifierSelections[contextModifierKey];
          
          // Merge local selection (IDs) with context data (text fields)
          let enrichedModifierSelection = null;
          if (localModifierSelection || contextModifierData) {
            enrichedModifierSelection = {
              ...localModifierSelection,
              ...contextModifierData, // This will include modifierName, modifierPrompt, modifierPrice
            };
          }

        const isMerchandiseUpsell = upsell.pageType === 'Merchandise';

        return {
          ...item,
            quantity: 1, // Each item represents one person
            pageType: upsell.pageType || 'EventTemplate',
            // Merchandise uses the main page ID; EventTemplate uses the upsell's own page ID
            pageId: isMerchandiseUpsell ? pageId : upsell.id,
            selectedDate: selectedEventTime?.startDateTime || (formatDateLocal(selectedDate) + 'T10:00:00'),
          selectedEventTime: selectedEventTime,
            eventId: selectedEventTime?.eventId || null,
            eventCapacityUsageId: selectedEventTime?.eventCapacityUsageId || null,
          questionAnswers: questionAnswers,
            eventQuestions: eventQuestions?.eventQuestions || [],
            participantDetails: participantDetails,
            participantAnswers: participantAnswers,
          modifierSelection: enrichedModifierSelection,
          allergenNotes: allergenNotes[modifierKey] || null,
          parentUpsellId: upsell.id,
            parentUpsellName: upsell.name,
            // For merchandise, parentWebSaleItemId links this item to the triggering ticket
            parentWebSaleItemId: upsell.parentWebSaleItemId || null
          };
        });
      });

    // Format questions for Venpos API (if any were answered)
    const formattedQuestions = [];


    // eventQuestions is response.data, which has eventQuestions array directly
    if (eventQuestions?.eventQuestions && Object.keys(questionAnswers).length > 0) {

      // Total instances equals total quantity selected for this upsell
      const totalQuantity = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
      // Deduplicate base questions by questionId to avoid multiplying duplicates by totalQuantity
      const uniqueQuestionsMap = {};
      eventQuestions.eventQuestions.forEach(q => {
        if (!uniqueQuestionsMap[q.questionId]) uniqueQuestionsMap[q.questionId] = q;
      });
      const uniqueQuestions = Object.values(uniqueQuestionsMap);
      uniqueQuestions.forEach((question, qIdx) => {
        for (let occ = 0; occ < totalQuantity; occ += 1) {
          const qKey = makeQuestionKey(question.questionId, occ);
          const answer = questionAnswers[qKey];

          if (answer) {
            formattedQuestions.push({
              id: question.questionId,
              question: question.question,
              value: answer,
              eventId: selectedEventTime?.eventId || null,
              configurationId: question.configurationId || null,
              index: occ + 1, // match working example (1-based)
              productId: question.productId || null
            });
          }
        }
      });

    } else {

    }
    
    // Log participant details for debugging
    if (participantDetails && Object.keys(participantAnswers).length > 0) {


    }

    if (itemsToAdd.length > 0) {
      onAddToBasket(itemsToAdd, formattedQuestions);
      onClose();
    }
  };

  const loadEventQuestions = async () => {
    if (!selectedEventTime) {

      return;
    }

    setLoadingQuestions(true);
    try {
      // Prepare webSaleItems object INCLUDING ALL BASKET ITEMS (like Venpos working example)
      const webSaleItems = {};
      const eventIds = [];

      // First, add existing basket items (main tickets ONLY - exclude already-added upsells)
      selectedTickets.forEach((ticket, idx) => {

        // SKIP upsell items - only include main tickets
        if (ticket.isUpsellItem) {

          return;
        }
        
        if (ticket.webSaleItemId && (ticket.quantity || 0) > 0) {
          webSaleItems[ticket.webSaleItemId] = ticket.quantity;

          // Add eventId if present and not already in array
          if (ticket.eventId && !eventIds.includes(ticket.eventId)) {
            eventIds.push(ticket.eventId);

          }
        }
      });

      // Then, add current upsell items being selected
      Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .forEach(([itemId, quantity]) => {
          webSaleItems[itemId] = quantity;

        });

      // Add current upsell eventId
      if (selectedEventTime.eventId && !eventIds.includes(selectedEventTime.eventId)) {
        eventIds.push(selectedEventTime.eventId);

      }

      const response = await venposService.getEventQuestions(
        '978', // locationId
        webSaleItems,
        eventIds
      );

      const allQuestions = response?.data?.eventQuestions || [];
      const isFestiveAfternoonTea =
        isFestiveAfternoonTeaLabel(upsell?.name) ||
        Object.entries(selectedItems).some(([itemId, quantity]) => {
          if (!quantity) return false;
          const item = availableItems.find(i => i.webSaleItemId === itemId);
          return isFestiveAfternoonTeaLabel(item?.name);
        });
      // Festive Afternoon Tea collects allergen notes per guest — skip the Venpos allergen questions page.
      // Regular Aviary afternoon tea already has its own flow and should be left unchanged.
      const questionsToShow = isFestiveAfternoonTea
        ? allQuestions.filter(q => !isAllergenEventQuestion(q))
        : allQuestions;

      if (questionsToShow.length > 0) {
        setEventQuestions({ ...(response.data || {}), eventQuestions: questionsToShow });
        setCurrentStep('questions');
      } else {
        setEventQuestions(null);
        loadProductModifiers();
      }
    } catch (error) {

      // Fallback: proceed to modifiers or basket
      loadProductModifiers();
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleContinueToTimes = () => {
    // Merchandise upsells have no event time slots — add directly to basket
    if (upsell?.pageType === 'Merchandise') {
      handleAddToBasket();
      return;
    }

    const selectedItemsList = Object.entries(selectedItems)
      .filter(([_, quantity]) => quantity > 0)
      .map(([itemId, quantity]) => {
        const item = availableItems.find(i => i.webSaleItemId === itemId);
        return item ? { ...item, quantity } : null;
      });

    const filteredList = selectedItemsList.filter(Boolean);

    if (filteredList.length > 0) {
      loadEventTimes(filteredList);
    }
  };

  const loadProductModifiers = async () => {
    setLoadingModifiers(true);
    try {
      // Prepare items for the API call
      const items = Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .map(([itemId, quantity]) => {
          const item = availableItems.find(i => i.webSaleItemId === itemId);
          if (!item) {
            return null;
          }
          return {
            pageType: 'WebPage',
            pageId: upsell.parentWebSaleItemId || pageId,
            webSaleItemId: item.webSaleItemId,
            quantity: quantity,
            webSaleItemTypeID: item.webSaleItemTypeID,
            name: item.name,
            selectedDate: selectedEventTime?.startDateTime || (formatDateLocal(selectedDate) + 'T10:00:00'),
            reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
            // CRITICAL FIX: Extract correct event metadata from selectedEventTime
            eventCapacityUsageId: selectedEventTime?.eventCapacityUsageId || null,
            eventId: selectedEventTime?.eventId || null,
            fixedDonationAmount: 0,
            giftAidEligible: false,
            isSysGift: false,
            isSysMemberCard: false
          };
        });

      const sanitizedItems = items.filter(Boolean);
      const isTeaUpsell =
        isAfternoonTeaBookingLabel(upsell?.name) ||
        sanitizedItems.some(item => isAfternoonTeaBookingLabel(item.name));
      const response = await venposService.getProductModifiers(String(locationId), sanitizedItems);

      if (response?.data && response.data.length > 0) {
        setProductModifiers(response.data);
        setCurrentStep('modifiers');
      } else if (isTeaUpsell) {
        // Festive Afternoon Tea (and Aviary) still need per-guest allergen notes
        // even if Venpos has no dietary modifiers on the product.
        setProductModifiers([]);
        setCurrentStep('modifiers');
      } else {
        // No modifiers, proceed directly to basket
        handleAddToBasket();
      }
    } catch (error) {
      const isTeaUpsell =
        isAfternoonTeaBookingLabel(upsell?.name) ||
        Object.entries(selectedItems).some(([itemId, quantity]) => {
          if (!quantity) return false;
          const item = availableItems.find(i => i.webSaleItemId === itemId);
          return isAfternoonTeaBookingLabel(item?.name);
        });
      if (isTeaUpsell) {
        setProductModifiers([]);
        setCurrentStep('modifiers');
      } else {
        // Fallback: proceed without modifiers
        handleAddToBasket();
      }
    } finally {
      setLoadingModifiers(false);
    }
  };

  const handleContinueToQuestions = () => {
    if (selectedEventTime) {
      // Only clear if we're reloading (already have questions)
      if (eventQuestions) {

        setEventQuestions(null);
        setQuestionAnswers({});
      }
      loadEventQuestions();
    }
  };

  const loadParticipantDetails = async () => {
    setLoadingParticipants(true);
    try {
      // Build items array for participant details call - include ALL basket items
      const allItems = [];
      
      // Add existing basket items (main tickets) - only non-upsell items
      selectedTickets.forEach(ticket => {
        if (ticket.isUpsellItem) return; // Skip already-added upsells
        
        if (ticket.webSaleItemId && (ticket.quantity || 0) > 0) {
          allItems.push({
            pageType: ticket.pageType || 'WebPage',
            pageId: ticket.pageId,
            fixedDonationAmount: ticket.fixedDonationAmount || 0,
            giftAidEligible: ticket.giftAidEligible || false,
            webSaleItemId: ticket.webSaleItemId,
            quantity: ticket.quantity,
            webSaleItemTypeID: ticket.webSaleItemTypeID,
            name: ticket.name,
            isSysGift: ticket.isSysGift || false,
            isSysMemberCard: ticket.isSysMemberCard || false,
            selectedDate: ticket.selectedDate,
            eventId: ticket.eventId,
            eventCapacityUsageId: ticket.eventCapacityUsageId,
            reservationExpiryDateTime: ticket.reservationExpiryDateTime,
            ...(ticket.parentWebSaleItemId && { parentWebSaleItemId: ticket.parentWebSaleItemId })
          });
        }
      });
      
      // Add current upsell items
      Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .forEach(([itemId, quantity]) => {
          const item = availableItems.find(i => i.webSaleItemId === itemId);
          if (item) {
            // Find parent ticket to link upsell to
            const parentTicket = selectedTickets.find(t => t.quantity > 0 && !t.isUpsellItem);
            
            allItems.push({
              pageType: upsell.pageType || 'EventTemplate',
              pageId: upsell.id,
              webSaleItemId: item.webSaleItemId,
              webSaleItemTypeID: item.webSaleItemTypeID,
              quantity: quantity,
              name: item.name,
              selectedDate: selectedEventTime.startDateTime,
              eventId: selectedEventTime.eventId,
              eventCapacityUsageId: selectedEventTime.eventCapacityUsageId,
              reservationExpiryDateTime: selectedEventTime.reservationExpiryDateTime,
              fixedDonationAmount: item.fixedDonationAmount || 0,
              giftAidEligible: item.giftAidEligible || false,
              isSysGift: item.isSysGift || false,
              isSysMemberCard: item.isSysMemberCard || false,
              ...(parentTicket?.webSaleItemId && { parentWebSaleItemId: parentTicket.webSaleItemId })
            });
          }
        });

      const response = await venposService.getEventParticipantDetails('978', allItems);

      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {

        setParticipantDetails(response.data);
        setCurrentStep('participants');
      } else {

        // No participant details needed, proceed to modifiers
        loadProductModifiers();
      }
    } catch (error) {

      // Fallback: proceed to modifiers
      loadProductModifiers();
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleContinueToModifiers = () => {
    // First check if we need to collect participant details
    loadParticipantDetails();
  };
  
  const handleContinueFromParticipantsToModifiers = () => {
    loadProductModifiers();
  };

  const handleBackToTimes = () => {
    // Clear questions and answers when going back to times
    setEventQuestions(null);
    setQuestionAnswers({});
    setCurrentStep('times');
  };

  // Build a stable per-instance key for duplicate product questions
  const makeQuestionKey = (questionId, occurrenceIndex) => `${questionId}__${occurrenceIndex}`;

  const handleQuestionAnswerChange = (questionKey, answer) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionKey]: answer
    }));
  };

  const handleParticipantAnswerChange = (participantId, fieldName, value) => {
    setParticipantAnswers(prev => ({
      ...prev,
      [participantId]: {
        ...prev[participantId],
        [fieldName]: value
      }
    }));
  };
  
  // Check if all required questions are answered
  const areRequiredQuestionsAnswered = () => {
    if (!eventQuestions?.eventQuestions) return true;
    const requiredQuestions = eventQuestions.eventQuestions.filter(q => q.isRequired);
    if (requiredQuestions.length === 0) return true;

    // Each duplicate instance of the same questionId must be answered
    const occurrenceMap = {};
    let allAnswered = true;
    for (const q of requiredQuestions) {
      const occ = occurrenceMap[q.questionId] || 0;
      const key = makeQuestionKey(q.questionId, occ);
      occurrenceMap[q.questionId] = occ + 1;
      const answer = questionAnswers[key];
      if (!answer || String(answer).trim() === '') {
        allAnswered = false;
        break;
      }
    }

    return allAnswered;
  };
  
  // Check if all required participant details are filled
  const areParticipantDetailsComplete = () => {
    if (!participantDetails || participantDetails.length === 0) return true;
    
    let allComplete = true;
    
    participantDetails.forEach((event, eventIndex) => {
      if (!event.eventItemDetails) return;
      
      event.eventItemDetails.forEach((itemDetail, itemIndex) => {
        if (!itemDetail.participantDetails) return;
        
        itemDetail.participantDetails.forEach((participant, pIndex) => {
          const participantId = `${event.eventId}-${itemIndex}-${pIndex}`;
          const answers = participantAnswers[participantId] || {};
          
          // Check required fields (firstName and lastName are always required for participants)
          if (!answers.firstName || answers.firstName.trim() === '') {
            allComplete = false;
          }
          if (!answers.lastName || answers.lastName.trim() === '') {
            allComplete = false;
          }
        });
      });
    });

    return allComplete;
  };

  // Check if all required modifiers are selected for each quantity
  const areRequiredModifiersSelected = () => {
    if (!productModifiers) return true;
    
    let allComplete = true;
    
    // Check each selected item and quantity
    Object.entries(selectedItems)
      .filter(([_, quantity]) => quantity > 0)
      .forEach(([itemId, quantity]) => {
        // Check each quantity (Person 1, Person 2, etc.)
        for (let quantityIndex = 0; quantityIndex < quantity; quantityIndex++) {
          productModifiers.forEach(modifier => {
            // Check if this modifier is required
            if (modifier.minimumSelectionsAllowed > 0) {
              // Use the same key scheme used when storing selections:
              // `${itemId}-${quantityIndex}` so lookups align with handleModifierSelection/rendering
              const modifierKey = `${itemId}-${quantityIndex}`;
              const selection = localModifierSelections[modifierKey];
              
              if (!selection || !selection.modifierTextId) {
                allComplete = false;
              }
            }
          });
        }
      });
    
    return allComplete;
  };

  const handleModifierSelection = async (modifierId, modifierTextId, itemId, quantityIndex = 0) => {
    // Create a unique key for each quantity (e.g., "itemId-0", "itemId-1")
    const modifierKey = `${itemId}-${quantityIndex}`;
    
    // Find the modifier and text for detailed logging
    const modifier = productModifiers?.find(m => m.id === modifierId);
    const modifierText = modifier?.productModifierTexts?.find(t => t.id === modifierTextId);

    const newSelections = {
      ...localModifierSelections,
      [modifierKey]: {
        modifierId,
        modifierTextId,
        quantityIndex,
        itemId
      }
    };
    
    setLocalModifierSelections(newSelections);
    
    // Store modifier selection in booking context for later use at checkout
    // Include both the prompt/question text and the selected option name
    const modifierData = {
      modifierId,
      modifierTextId,
      modifierName: modifierText?.name || 'Selected Option',
      modifierPrompt: modifier?.prompts || '', // The question/prompt text (e.g., "Dietary Requirements")
      modifierPrice: modifierText?.price || 0,
      quantityIndex
    };
    
    dispatch({
      type: BOOKING_ACTIONS.SET_MODIFIER_SELECTION,
      payload: {
        webSaleItemId: `${itemId}-${quantityIndex}`, // Unique key per quantity
        modifierData
      }
    });

  };

  const hasSelectedItems = Object.values(selectedItems).some(qty => qty > 0);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ModalTitle>
            {isHalloweenCraftsUpsellName(upsell?.name)
              ? HALLOWEEN_CRAFTS_DISPLAY_NAME
              : (upsell?.name || 'Experience Details')}
          </ModalTitle>
          {upsell?.description && (
            <ModalDescription>{upsell.description}</ModalDescription>
          )}
        </ModalHeader>

        <ModalContent>
          <StepIndicator>
            <StepDot $isActive={currentStep === 'items'} />
            <StepDot $isActive={currentStep === 'times'} />
            <StepDot $isActive={currentStep === 'questions'} />
            <StepDot $isActive={currentStep === 'participants'} />
            <StepDot $isActive={currentStep === 'modifiers'} />
          </StepIndicator>

          {loadingDetails ? (
            <LoadingSpinner>Loading experience details...</LoadingSpinner>
          ) : currentStep === 'items' ? (
            availableItems.length > 0 ? (
              <>
                {isSantasGrottoCastleUpsell(upsell?.name) && (
                  <GrottoCastleNote>
                    Entry ticket required. Children’s Grotto tickets only — accompanying adults do not need a Grotto ticket.
                  </GrottoCastleNote>
                )}
                {isRequiredAfternoonTeaUpsell && mainTicketCount > 0 && (
                  <GrottoCastleNote>
                    Afternoon tea is not included with Castle entry. Add as many as you need, up to {afternoonTeaPartyCap} {afternoonTeaPartyCap === 1 ? 'tea' : 'teas'} — you cannot add more than your entry tickets.
                  </GrottoCastleNote>
                )}
                <ItemsGrid>
                  {availableItems.map((item) => {
                    const isSelected = (selectedItems[item.webSaleItemId] || 0) > 0;
                    const selectedQuantity = selectedItems[item.webSaleItemId] || 0;
                    const maxAllowed = getMaxQuantityForItem(item);
                    const nameTrim = (item.name || '').trim();
                    const descTrim = (item.description || '').trim();
                    const showDescription =
                      descTrim.length > 0 && descTrim.toLowerCase() !== nameTrim.toLowerCase();

                    return (
                      <ItemCard 
                        key={item.webSaleItemId}
                        $isSelected={isSelected}
                        onClick={() => {
                          const currentQty = selectedItems[item.webSaleItemId] || 0;
                          if (currentQty < maxAllowed) {
                            handleItemQuantityChange(item.webSaleItemId, currentQty + 1);
                          }
                        }}
                      >
                        {isSelected && <SelectedBadge>{selectedQuantity}</SelectedBadge>}
                        
                        <ItemName>{item.name}</ItemName>
                        
                        {isAviaryAfternoonTeaUpsell(upsell?.name) &&
                          isGentlemansAfternoonTeaProduct(item.name) && (
                          <FathersDaySpecialNote>Father's Day Special</FathersDaySpecialNote>
                        )}
                        
                        {showDescription && (
                          <ItemDescription>{item.description}</ItemDescription>
                        )}
                        
                        {isAnimalParkUpsell &&
                          isAnimalParkFreePromotion(item.name) &&
                          maxAllowed > 0 && (
                            <ItemMetaNote>Max {maxAllowed}</ItemMetaNote>
                          )}
                        {isRequiredAfternoonTeaUpsell && (
                          <ItemMetaNote>Max {maxAllowed} remaining</ItemMetaNote>
                        )}
                        
                        <ItemCardFooter>
                          <ItemPrice $wellness={isWellnessDiscoveryFreeUpsell(upsell?.name)}>
                            {isWellnessDiscoveryFreeUpsell(upsell?.name)
                              ? 'Free'
                              : `£${item.price.toFixed(2)}`}
                          </ItemPrice>
                          <QuantitySelector onClick={(e) => e.stopPropagation()}>
                            <QuantityButton 
                              onClick={() => decrementQuantity(item.webSaleItemId)}
                              disabled={selectedQuantity <= 0}
                            >
                              −
                            </QuantityButton>
                            
                            <QuantityInput
                              type="number"
                              min="0"
                              max={maxAllowed}
                              value={selectedQuantity}
                              onChange={(e) => handleQuantityInputChange(item.webSaleItemId, e.target.value)}
                            />
                            
                            <QuantityButton 
                              onClick={() => incrementQuantity(item.webSaleItemId)}
                              disabled={selectedQuantity >= maxAllowed}
                            >
                              +
                            </QuantityButton>
                          </QuantitySelector>
                        </ItemCardFooter>
                      </ItemCard>
                    );
                  })}
                </ItemsGrid>

              </>
            ) : (
              <NoItemsMessage>
                <p>
                  Santa's Grotto toddler tickets aren’t available for {formattedVisitDate || 'this date'}.
                  Please choose a different visit date to unlock Toddler Special slots.
                </p>
                {onChangeDateClick && (
                  <NoItemsAction onClick={onChangeDateClick}>
                    Choose another date
                  </NoItemsAction>
                )}
              </NoItemsMessage>
            )
          ) : currentStep === 'times' && eventTimes?.events ? (
            <TimeSelectionContainer>
              <ModalTitle>Choose Your Time</ModalTitle>
              <ModalDescription>
                {upsell?.name && String(upsell.name).toLowerCase().includes('circus workshops')
                  ? '11:30 - 15:30 come and go as much as you like throughout your day'
                  : 'Select your preferred time for this experience'}
              </ModalDescription>
              
              {loadingTimes ? (
                <LoadingSpinner>Loading available times...</LoadingSpinner>
              ) : (() => {
                // Calculate total selected quantity
                const totalSelectedQuantity = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
                
                // Filter times based on main entry time
                const filteredEvents = eventTimes.events.filter((event) => {
                  // Filter out times that are before the main entry time
                  if (!selectedTimeSlot?.time) return true; // Show all if no main time
                  
                  const eventDateTime = new Date(event.startDateTime);
                  const eventTimeString = eventDateTime.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  // Convert times to minutes for comparison
                  const parseTime = (timeStr) => {
                    const [hours, minutes] = timeStr.split(':').map(Number);
                    return hours * 60 + minutes;
                  };
                  
                  const mainTimeMinutes = parseTime(selectedTimeSlot.time);
                  const eventTimeMinutes = parseTime(eventTimeString);
                  
                  // Only show times >= main entry time
                  return eventTimeMinutes >= mainTimeMinutes;
                });

                // Check if all times are sold out
                const allSoldOut = filteredEvents.length > 0 && filteredEvents.every(e => e.quantityAvailable === 0);
                
                // Show message if no times available after filtering
                if (filteredEvents.length === 0 || allSoldOut) {
                  return (
                    <NoItemsMessage style={{ marginTop: '1rem', background: allSoldOut ? 'rgba(255,243,205,0.7)' : undefined }}>
                      <p style={{ fontWeight: 700, marginBottom: '0.4rem', color: allSoldOut ? '#7d5a00' : undefined }}>
                        {allSoldOut ? 'Sold Out' : 'No Available Times'}
                      </p>
                      <p style={{ fontSize: '0.88rem', marginBottom: onChangeDateClick ? '0.85rem' : 0, opacity: 0.88 }}>
                        {allSoldOut
                          ? 'All time slots for this experience are fully booked on your selected date.'
                          : `No times available after your main entry time of ${selectedTimeSlot?.time}.`}
                      </p>
                      {onChangeDateClick && (
                        <NoItemsAction onClick={(e) => { e.stopPropagation(); onClose(); onChangeDateClick(); }}>
                          Change date
                        </NoItemsAction>
                      )}
                    </NoItemsMessage>
                  );
                }

                return (
                  <TimeGrid>
                    {filteredEvents.map((event) => {
                    const isSelected = selectedEventTime?.eventId === event.eventId;
                    const isSoldOut = event.quantityAvailable === 0;
                    const isDisabled = isSoldOut || totalSelectedQuantity > event.quantityAvailable;
                    const timeString = new Date(event.startDateTime).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    const isLimitedAvailability = !isDisabled && event.quantityAvailable <= 4;
                    
                    return (
                      <TimeOption 
                        key={event.eventId}
                        $isSelected={isSelected}
                        $isDisabled={isDisabled}
                        onClick={() => !isDisabled && setSelectedEventTime(event)}
                      >
                        <TimeDisplay $isDisabled={isDisabled}>{timeString}</TimeDisplay>
                        <TimeDescription $isDisabled={isDisabled}>{event.description}</TimeDescription>
                        <TimeAvailability $isDisabled={isDisabled} $isLimited={isLimitedAvailability}>
                          {isSoldOut
                            ? 'Sold Out'
                            : isDisabled 
                              ? `Not enough spots (${event.quantityAvailable} available, ${totalSelectedQuantity} needed)` 
                              : isLimitedAvailability
                                ? `Limited Availability - Only ${event.quantityAvailable} spot${event.quantityAvailable === 1 ? '' : 's'} left!`
                                : `${event.quantityAvailable} spots available`
                          }
                        </TimeAvailability>
                      </TimeOption>
                    );
                  })}
                  </TimeGrid>
                );
              })()}
            </TimeSelectionContainer>
          ) : currentStep === 'questions' && eventQuestions?.eventQuestions ? (
            <QuestionsContainer>
              <ModalTitle>Additional Information</ModalTitle>
              <ModalDescription>Please answer the following questions for your booking</ModalDescription>
              
              {loadingQuestions ? (
                <LoadingSpinner>Loading questions...</LoadingSpinner>
              ) : (
                <>
                  {(() => {
                    const occurrenceMap = {};
                    return eventQuestions.eventQuestions.map((question) => {
                      const occ = occurrenceMap[question.questionId] || 0;
                      const qKey = makeQuestionKey(question.questionId, occ);
                      occurrenceMap[question.questionId] = occ + 1;
                      return (
                        <QuestionItem key={`${question.questionId}_${occ}`}>
                      <QuestionText>
                        {question.question}
                        {question.isRequired && <RequiredIndicator>*</RequiredIndicator>}
                      </QuestionText>
                      {question.controlType === 'TextBox' ? (
                        <QuestionInput
                          type="text"
                              value={questionAnswers[qKey] || ''}
                              $isRequired={question.isRequired}
                              onChange={(e) => handleQuestionAnswerChange(qKey, e.target.value)}
                          placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                        />
                          ) : question.controlType === 'DropDownList' && question.answers ? (
                            <QuestionInput
                              as="select"
                              value={questionAnswers[qKey] || ''}
                              $isRequired={question.isRequired}
                              onChange={(e) => handleQuestionAnswerChange(qKey, e.target.value)}
                            >
                              <option value="">Please select{question.isRequired ? ' (required)' : ''}</option>
                              {question.answers.map((answer) => (
                                <option key={answer.optionId} value={answer.answerValue}>
                                  {answer.answerText}
                                </option>
                              ))}
                            </QuestionInput>
                      ) : (
                        <QuestionTextArea
                              value={questionAnswers[qKey] || ''}
                              $isRequired={question.isRequired}
                              onChange={(e) => handleQuestionAnswerChange(qKey, e.target.value)}
                          placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                        />
                      )}
                    </QuestionItem>
                      );
                    });
                  })()}
                </>
              )}
            </QuestionsContainer>
          ) : currentStep === 'participants' && participantDetails ? (
            <QuestionsContainer>
              <ModalTitle>Participant Information</ModalTitle>
              <ModalDescription>Please provide details for each participant</ModalDescription>
              
              {loadingParticipants ? (
                <LoadingSpinner>Loading participant details...</LoadingSpinner>
              ) : (
                <>
                  {participantDetails.map((event, eventIndex) => (
                    <div key={event.eventId || eventIndex} style={{ marginBottom: '2rem' }}>
                      <QuestionText style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#2c3e50' }}>
                        {event.eventName || event.description}
                      </QuestionText>
                      
                      {event.eventItemDetails && event.eventItemDetails.map((itemDetail, itemIndex) => (
                        <div key={`${itemDetail.itemName}-${itemIndex}`} style={{ marginBottom: '1.5rem' }}>
                          {itemDetail.itemName && (
                            <QuestionText style={{ fontSize: '1.05rem', marginBottom: '0.75rem', opacity: 0.8 }}>
                              {itemDetail.itemName}
                            </QuestionText>
                          )}
                          
                          {itemDetail.participantDetails && itemDetail.participantDetails.map((participant, pIndex) => {
                            const participantId = `${event.eventId}-${itemIndex}-${pIndex}`;
                            
                            return (
                              <div key={participantId} style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                <QuestionText style={{ fontSize: '0.95rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                                  {participant.isLeadParticipant ? 'Lead Participant' : `Participant ${pIndex + 1}`}
                                </QuestionText>
                                
                                {/* First Name */}
                                <QuestionItem>
                                  <QuestionText>
                                    First Name
                                    <RequiredIndicator>*</RequiredIndicator>
                                  </QuestionText>
                                  <QuestionInput
                                    type="text"
                                    autoCapitalize="words"
                                    value={participantAnswers[participantId]?.firstName || ''}
                                    isRequired={true}
                                    onChange={(e) => handleParticipantAnswerChange(participantId, 'firstName', e.target.value)}
                                    placeholder="Enter first name (required)"
                                  />
                                </QuestionItem>
                                
                                {/* Last Name */}
                                <QuestionItem>
                                  <QuestionText>
                                    Last Name
                                    <RequiredIndicator>*</RequiredIndicator>
                                  </QuestionText>
                                  <QuestionInput
                                    type="text"
                                    autoCapitalize="words"
                                    value={participantAnswers[participantId]?.lastName || ''}
                                    isRequired={true}
                                    onChange={(e) => handleParticipantAnswerChange(participantId, 'lastName', e.target.value)}
                                    placeholder="Enter last name (required)"
                                  />
                                </QuestionItem>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </QuestionsContainer>
          ) : currentStep === 'modifiers' && productModifiers ? (
            <ModifiersContainer>
              <ModalTitle>
                {productModifiers.length > 0 ? 'Select Options' : 'Allergen Information'}
              </ModalTitle>
              <ModalDescription>
                {productModifiers.length > 0
                  ? 'Choose your dietary requirements and preferences'
                  : 'Please share any allergen or dietary requirements for each guest'}
              </ModalDescription>
              
              {loadingModifiers ? (
                <LoadingSpinner>Loading options...</LoadingSpinner>
              ) : (
                <>
                  {(() => {
                    // Debug: Log modifiers structure

                    // Deduplicate modifiers by ID AND prompts to prevent duplicate sections
                    // Sometimes API returns same modifier with different IDs for different items
                    const uniqueModifiers = productModifiers?.filter((modifier, index, self) => 
                      index === self.findIndex(m => 
                        m.id === modifier.id || 
                        (m.prompts === modifier.prompts && m.name === modifier.name)
                      )
                    ) || [];

                    // Calculate total quantity across all items
                    const totalQuantity = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);

                    return Array.from({ length: totalQuantity }, (_, personIndex) => {
                      // Find which item this person belongs to
                      let currentItemId = null;
                      let itemQuantityIndex = personIndex;
                      let currentItem = null;
                      
                      for (const [itemId, quantity] of Object.entries(selectedItems)) {
                        if (itemQuantityIndex < quantity) {
                          currentItemId = itemId;
                          currentItem = availableItems.find(i => i.webSaleItemId === itemId);
                          break;
                        }
                        itemQuantityIndex -= quantity;
                      }

                      return (
                        <div key={`person-${personIndex}`}>
                          <h4 style={{ marginBottom: '1rem', color: '#333' }}>
                            Person {personIndex + 1} - {currentItem?.name || 'Item'}
                          </h4>
                          {uniqueModifiers.map((modifier, modifierIndex) => {
                            // Debug each modifier being rendered

                            return (
                              <ModifierGroup key={`${modifier.id}-${personIndex}-${modifierIndex}`}>
                      <ModifierPrompt>
                        {modifier.prompts}
                        {modifier.minimumSelectionsAllowed > 0 && (
                          <RequiredIndicator>*</RequiredIndicator>
                        )}
                      </ModifierPrompt>
                      
                      <ModifierOptions>
                        {modifier.productModifierTexts.map((modifierText) => {
                                  const modifierKey = `${currentItemId}-${itemQuantityIndex}`;
                                  const isSelected = localModifierSelections[modifierKey]?.modifierTextId === modifierText.id;

                          return (
                            <ModifierOption
                                      key={`${modifierText.id}-${personIndex}`}
                              $isSelected={isSelected}
                                      onClick={() => {

                                        handleModifierSelection(modifier.id, modifierText.id, currentItemId, itemQuantityIndex);
                                      }}
                            >
                              <ModifierName>{modifierText.name}</ModifierName>
                              <ModifierPrice>
                                {modifierText.price > 0 ? `+£${modifierText.price.toFixed(2)}` : 'Included'}
                              </ModifierPrice>
                            </ModifierOption>
                          );
                        })}
                      </ModifierOptions>
                    </ModifierGroup>
                            );
                          })}
                          
                          {/* Allergen Notes Section - appears after all modifiers for this person */}
                          <AllergenNotesContainer>
                            <AllergenLabel htmlFor={`allergen-${currentItemId}-${itemQuantityIndex}`}>
                              Allergen Information
                            </AllergenLabel>
                            <AllergenTextarea
                              id={`allergen-${currentItemId}-${itemQuantityIndex}`}
                              placeholder="Please provide any allergen information or dietary requirements for this afternoon tea..."
                              value={allergenNotes[`${currentItemId}-${itemQuantityIndex}`] || ''}
                              onChange={(e) => {
                                const key = `${currentItemId}-${itemQuantityIndex}`;
                                setAllergenNotes(prev => ({
                                  ...prev,
                                  [key]: e.target.value
                                }));
                              }}
                            />
                          </AllergenNotesContainer>
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </ModifiersContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <p>No items available for this experience.</p>
            </div>
          )}
        </ModalContent>

        {itemDetails?.webSaleItems && (
          <ModalFooter>
            {currentStep === 'items' ? (
              <>
                <TotalAmount>
                  {hasSelectedItems ? `Total: £${calculateTotal().toFixed(2)}` : 'Select items to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <CancelButton onClick={onClose}>
                    Cancel
                  </CancelButton>
                  {(selectedTickets || []).some(t => (t.isUpsellItem || t.pageType === 'EventTemplate') && (t.parentUpsellId === upsell?.id || t.pageId === upsell?.id)) && (
                    <CancelButton onClick={() => {
                      if (onRemoveUpsell && upsell?.id) onRemoveUpsell(upsell.id);
                      onClose();
                    }}>
                      Remove from Basket
                    </CancelButton>
                  )}
                  
                  <AddToBasketButton 
                    onClick={handleContinueToTimes}
                    disabled={!hasSelectedItems || loadingTimes}
                  >
                    {loadingTimes ? 'Loading...' : 'Continue'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'times' ? (
              <>
                <TotalAmount>
                  {selectedEventTime ? `Selected: ${new Date(selectedEventTime.startDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Select a time to continue'}
                </TotalAmount>
                
                <ActionButtons ref={navigationRef}>
                  <BackButton onClick={() => setCurrentStep('items')}>
                    ← Back to Items
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueToQuestions}
                    disabled={!selectedEventTime || loadingQuestions}
                  >
                    {loadingQuestions ? 'Loading...' : 'Continue'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'questions' ? (
              <>
                <TotalAmount isError={!areRequiredQuestionsAnswered()}>
                  {areRequiredQuestionsAnswered() 
                    ? 'All required questions answered ✓' 
                    : 'Please answer all required questions (*) to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={handleBackToTimes}>
                    ← Back to Times
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueToModifiers}
                    disabled={loadingParticipants || !areRequiredQuestionsAnswered()}
                  >
                    {loadingParticipants ? 'Loading...' : 'Continue'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'participants' ? (
              <>
                <TotalAmount isError={!areParticipantDetailsComplete()}>
                  {areParticipantDetailsComplete() 
                    ? 'All participant details complete ✓' 
                    : 'Please fill all required participant details (*) to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={() => setCurrentStep(eventQuestions?.eventQuestions?.length ? 'questions' : 'times')}>
                    ← Back to {eventQuestions?.eventQuestions?.length ? 'Questions' : 'Times'}
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueFromParticipantsToModifiers}
                    disabled={loadingModifiers || !areParticipantDetailsComplete()}
                  >
                    {loadingModifiers ? 'Loading...' : 'Continue'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'modifiers' ? (
              <>
                <TotalAmount isError={!areRequiredModifiersSelected()}>
                  {areRequiredModifiersSelected()
                    ? (productModifiers && productModifiers.length > 0
                      ? 'All required options selected ✓'
                      : 'You can add allergen notes, or continue if none apply')
                    : 'Please select all required options (*) for each person to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={() => setCurrentStep(
                    participantDetails
                      ? 'participants'
                      : (eventQuestions?.eventQuestions?.length ? 'questions' : 'times')
                  )}>
                    ← Back to {participantDetails
                      ? 'Participant Details'
                      : (eventQuestions?.eventQuestions?.length ? 'Questions' : 'Times')}
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleAddToBasket}
                    disabled={loading || !areRequiredModifiersSelected()}
                  >
                    {loading ? 'Adding...' : 'Add to Basket'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : null}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>,
    document.body
  );
};

export default UpsellDetailModal;